// The vendor-free contract of catalog search (CHT-710, Phase 3.6): the request, the codes of an answer,
// and the numbers that rank it. Behavior (validation, fusion, boost, diversity) comes in later commits;
// this file freezes the names and shapes. A change after the freeze is additive.
// --- versions -------------------------------------------------------------------------------------------
/**
 * The version of the text a search document is built from (which fields, which weights, which folding).
 * It includes `FOLD_VERSION`. A new version needs a rebuild of every document.
 */
export const SEARCH_DOCUMENT_VERSION = 1;
// --- bounds ---------------------------------------------------------------------------------------------
export const CATALOG_SEARCH_BOUNDS = Object.freeze({
    /** Characters of `text` before folding. */
    textChars: 200,
    /** Word tokens of `text` after folding. */
    textTokens: 16,
    locality: 100,
    dietary: 10,
    categories: 10,
    preferenceTags: 5,
    codeChars: 64,
    limitDefault: 20,
    limitMax: 50,
    radiusMinMeters: 100,
    radiusMaxMeters: 200_000,
    /** The largest `within` box, in degrees of latitude and of longitude. */
    boxMaxDegrees: 20,
    partySizeMax: 100,
    eventWindowMaxDays: 366,
    /** Rows one signal (name, text, vector, distance) may bring into the fusion. */
    candidatesPerSignal: 200,
    statementTimeoutMs: 1500,
    /**
     * How long search waits for the query vector when the embedder names no time of its own
     * (`CatalogEmbedder.queryTimeoutMs`): the default of the fakes.
     */
    embedderTimeoutMs: 300,
    /**
     * The hard ceiling of that wait. An embedder may ask for more than the default (a real one answers in about
     * 400 ms), but never for more than this, so no embedder can hold a search longer.
     */
    embedderTimeoutMaxMs: 2000,
    /** Up to this many eligible rows the vector signal is an exact scan; above it, an HNSW index scan. */
    exactVectorMaxRows: 25_000,
    /** The stored vector size. Another size needs a migration. */
    embeddingDimensions: 384,
    /** What one `CatalogEmbedder.embed` call accepts. */
    embedderBatchTexts: 32,
    embedderTextChars: 8000,
    /** The size of each text part of a stored document (characters, after folding). */
    document: Object.freeze({ name: 200, terms: 4000, summary: 1000, body: 8000 }),
});
// --- request --------------------------------------------------------------------------------------------
export const catalogSearchAudiences = Object.freeze(["CUSTOMER", "STAFF"]);
/** The names of the request fields that narrow or rank a search. Reasons and `notApplicable` use them. */
export const catalogSearchFilterNames = Object.freeze([
    "text",
    "types",
    "location",
    "near",
    "within",
    "price",
    "pricingModels",
    "dietary",
    "categories",
    "eventWindow",
    "partySize",
    "fulfilmentModes",
    "preferenceTags",
]);
// --- outcome and reasons --------------------------------------------------------------------------------
export const catalogSearchOutcomes = Object.freeze([
    "OK",
    "NO_MATCH",
    "CONFLICTING_CONSTRAINTS",
    "INVALID",
]);
/**
 * Filters that cannot all hold. Found before any SQL runs. The answer has no hits, and no filter is
 * relaxed to make one.
 */
export const catalogSearchConflictCodes = Object.freeze([
    "price.min_gt_max",
    "event_window.from_not_before_to",
    "party_size.not_applicable",
    "event_window.not_applicable",
    "near.outside_within",
]);
/** A request that breaks a rule the HTTP schema cannot check. The answer has no hits. */
export const catalogSearchInvalidCodes = Object.freeze([
    "text.too_many_tokens",
    "within.south_not_before_north",
    "within.west_not_before_east",
    "within.too_large",
    "event_window.too_long",
    "staff_field.not_allowed",
]);
/** Notes on an `OK` answer. */
export const catalogSearchNoticeCodes = Object.freeze(["NO_TEXT_MATCH"]);
/** What the vector signal did. `UNAVAILABLE`: the embedder is off or timed out, so only text and place ranked. */
export const catalogSearchSemanticStates = Object.freeze([
    "USED",
    "UNAVAILABLE",
    "NOT_REQUESTED",
]);
// --- embeddings -----------------------------------------------------------------------------------------
/** The stored states of an embedding row. */
export const catalogEmbeddingStates = Object.freeze(["PENDING", "READY", "FAILED"]);
/**
 * What staff see. `NONE`: no row for the active model. `STALE` is derived, never stored: the row is
 * `READY` and its vector came from an older document. A stale vector still orders candidates.
 */
export const catalogSearchEmbeddingStates = Object.freeze([
    "NONE",
    "PENDING",
    "READY",
    "STALE",
    "FAILED",
]);
// --- hit explanations (staff only) ----------------------------------------------------------------------
/** Why a hit is in the list. Staff see these; a customer never does. */
export const catalogSearchHitReasons = Object.freeze([
    "NAME_EXACT",
    "NAME_PHRASE",
    "NAME_PREFIX",
    "TEXT_MATCH",
    "SEMANTIC_MATCH",
    "WITHIN_RADIUS",
    "EDITORIAL_BOOST",
    "FRESHNESS_BOOST",
    "PREFERENCE_BOOST",
]);
/** Why a listing was folded into a better one. */
export const catalogSearchCollapseReasons = Object.freeze(["SAME_NAME", "SIMILAR_VECTOR"]);
// --- ranking v2 -----------------------------------------------------------------------------------------
/**
 * The ranking numbers of version 2 (CHT-1973 follow-up). The relevance suite protects them: a change that
 * lowers a metric fails the suite, and a threshold is never lowered. A change to a number is a new version.
 *
 * Fusion is weighted reciprocal rank: `fused = sum(weight / (rrfK + rank))` over the signals an item
 * appears in. An exact or phrase name hit gets `2 / 61`, which is more than any item without a name hit
 * can get from text and vector together (`1 / 61 + 0.7 / 61`, and `0.7 / 61 + 1.0 / 61` for a
 * descriptive query). The business boost is bounded: at most +20 %, so it reorders near ties and cannot
 * lift an item from rank 30 over rank 1.
 *
 * What v2 changed against v1 (the measured data is in `docs/engineering/search.md`):
 * - `descriptive`: a query of `minTokens` words or more is a description ("a quiet weekend to unwind"),
 *   not a name. A word that matches inside a long description is weak evidence there, so the vector
 *   signal outweighs the word signal. This holds only when a vector list is left to weigh: without one,
 *   the v1 weights keep the order of the lexical-only path.
 * - `vectorCutoff`: a vector hit that is weak for its model (below an absolute least, or not clearly above the
 *   median of the query) is noise and is dropped.
 */
export const SEARCH_RANKING_V2 = Object.freeze({
    version: "v2",
    rrfK: 60,
    weights: Object.freeze({ name: 2.0, lexical: 1.0, vector: 0.7, geo: 0.3 }),
    /** The weights of the text and vector signals for a descriptive query. Their sum stays under `weights.name`. */
    descriptive: Object.freeze({
        /** The count of query words (stop words left out) from which a query is descriptive. */
        minTokens: 3,
        weights: Object.freeze({ lexical: 0.7, vector: 1.0 }),
    }),
    /**
     * The cut-off of the vector signal, by embedding model key (`<model>:<adapter version>`). A vector hit counts
     * only when its cosine similarity (1 - cosine distance) to the query passes BOTH rules:
     *   1. `minSimilarity`: an absolute least value. It removes an off-topic text, which is far from everything.
     *   2. `marginOverMedian`: the hit must be at least this much above the MEDIAN similarity of all the eligible
     *      items that have a vector (the baseline of this query). It removes gibberish, which is close to
     *      everything by the same small amount. It applies only when the baseline has `minEmbedded` items or
     *      more: a median of a handful of items says nothing.
     * The scale of a similarity belongs to its model, so the numbers are tied to one key. A model key that is not
     * listed has NO cut-off (the fakes), and `null` marks a model that is known and not measured yet. A new model
     * or a new adapter version needs its own measured numbers: how they were chosen and how to choose them again
     * is in `docs/engineering/search.md`, "How to choose the cut-off again".
     *
     * `gemini-embedding-001-d384:1` (measured 2026-10-21, 60 curated items, 7 nonsense and 43 judged queries):
     * nonsense queries had a best similarity 0.023 to 0.035 above the median, every judged semantic query at
     * least 0.046; a margin from 0.036 to 0.045 keeps all of them apart, and 0.04 is the middle.
     */
    vectorCutoff: Object.freeze({
        "gemini-embedding-001-d384:1": Object.freeze({
            minSimilarity: 0.6,
            marginOverMedian: 0.04,
            minEmbedded: 8,
        }),
        "gemini-embedding-2-d384:1": null,
    }),
    /** The fallback order (editorial priority, then id) ranks an item only when no other signal ranks it. */
    fallbackEditorialWeight: 1.0,
    /** `final = fused * (1 + editorial * e + freshness * f + preference * p)`. */
    boost: Object.freeze({ editorial: 0.1, freshness: 0.05, preference: 0.05 }),
    /** `editorial = editorial_priority / editorialScale`. */
    editorialScale: 1000,
    /** Freshness is 1 up to `fullDays` since the last verification, then falls linearly to 0 at `zeroDays`. */
    freshness: Object.freeze({ fullDays: 180, zeroDays: 540 }),
    diversity: Object.freeze({
        /** At most `max(minPerGroup, ceil(limit / groupDivisor))` hits per item type and locality. */
        minPerGroup: 3,
        groupDivisor: 3,
        /** Cosine similarity at which two listings of one type and locality count as one. */
        duplicateCosine: 0.97,
    }),
});
